import React, { useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import apiConfig from '../../config/api.config';
import { 
  UserPlus, 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Building, 
  Truck, 
  AlertCircle, 
  ArrowLeft,
  CheckCircle,
  FileText,
  Shield,
  Settings,
  Globe,
  Upload,
  Lock,
  Check,
  X
} from 'lucide-react';

const RegisterContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: ${({ theme }) => theme.spacing.lg};
`;

const RegisterCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  padding: ${({ theme }) => theme.spacing.xl};
  width: 100%;
  max-width: 900px;
  max-height: 90vh;
  overflow-y: auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const LogoIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${({ theme }) => theme.spacing.md};
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  margin: 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`;

const Section = styled.div`
  background: ${({ theme }) => theme.colors.gray50};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
`;

const SectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 ${({ theme }) => theme.spacing.md} 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.md};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const InputGroup = styled.div<{ $fullWidth?: boolean }>`
  position: relative;
  grid-column: ${({ $fullWidth }) => $fullWidth ? '1 / -1' : 'auto'};
`;

const Label = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const RequiredMark = styled.span`
  color: ${({ theme }) => theme.colors.error};
  margin-left: 2px;
`;

const Input = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.md};
  border: 2px solid ${({ theme }) => theme.colors.gray200};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.textPrimary};
  transition: all 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}20;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.md};
  border: 2px solid ${({ theme }) => theme.colors.gray200};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.textPrimary};
  transition: all 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}20;
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.md};
  border: 2px solid ${({ theme }) => theme.colors.gray200};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.textPrimary};
  transition: all 0.2s ease;
  box-sizing: border-box;
  resize: vertical;
  min-height: 80px;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}20;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const CheckboxItem = styled.label`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  cursor: pointer;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  width: 18px;
  height: 18px;
  accent-color: ${({ theme }) => theme.colors.primary};
`;

const RadioGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md};
`;

// Tabla para tecnologías con porcentajes de participación
const TechTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const TechTableHeader = styled.thead`
  background: ${({ theme }) => theme.colors.gray100};
`;

const TechTableHeaderCell = styled.th`
  padding: ${({ theme }) => theme.spacing.sm};
  text-align: center;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textPrimary};
  border: 1px solid ${({ theme }) => theme.colors.gray300};
  font-size: 12px;
`;

const TechTableBody = styled.tbody``;

const TechTableRow = styled.tr`
  &:nth-child(even) {
    background: ${({ theme }) => theme.colors.gray50};
  }
  &:hover {
    background: ${({ theme }) => theme.colors.primary}10;
  }
`;

const TechTableCell = styled.td`
  padding: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.gray300};
  text-align: center;
`;

const TechNameCell = styled(TechTableCell)`
  text-align: left;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;

const RadioInput = styled.input.attrs({ type: 'radio' })`
  width: 16px;
  height: 16px;
  accent-color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
`;

const FileInputWrapper = styled.div`
  position: relative;
  display: inline-block;
  width: 100%;
`;

const FileInputLabel = styled.label`
  display: inline-block;
  padding: ${({ theme }) => theme.spacing.md};
  border: 2px dashed ${({ theme }) => theme.colors.gray300};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ theme }) => theme.colors.gray50};
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.gray100};
  }
`;

const FileInputHidden = styled.input`
  display: none;
`;

const FileName = styled.div`
  margin-top: ${({ theme }) => theme.spacing.xs};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;

const RadioItem = styled.label`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  cursor: pointer;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const Radio = styled.input.attrs({ type: 'radio' })`
  width: 18px;
  height: 18px;
  accent-color: ${({ theme }) => theme.colors.primary};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.xl};
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  flex: 1;
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.xs};

  ${({ variant, theme }) => {
    if (variant === 'secondary') {
      return `
        background: ${theme.colors.gray100};
        color: ${theme.colors.textSecondary};
        
        &:hover {
          background: ${theme.colors.gray200};
        }
      `;
    }
    return `
      background: ${theme.colors.primary};
      color: ${theme.colors.white};
      
      &:hover {
        background: ${theme.colors.primaryDark};
      }
    `;
  }}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Message = styled.div<{ type: 'error' | 'success' }>`
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  margin-bottom: ${({ theme }) => theme.spacing.md};

  ${({ type, theme }) => {
    if (type === 'error') {
      return `
        background: #fef2f2;
        color: #dc2626;
        border: 1px solid #fecaca;
      `;
    }
    return `
      background: #f0fdf4;
      color: #16a34a;
      border: 1px solid #bbf7d0;
    `;
  }}
`;

const PasswordRequirements = styled.div`
  background: ${({ theme }) => theme.colors.gray100};
  border: 1px solid ${({ theme }) => theme.colors.gray300};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

const RequirementTitle = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;
`;

const RequirementsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const RequirementItem = styled.li<{ $met: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ $met, theme }) => $met ? theme.colors.success : theme.colors.textSecondary};
  transition: color 0.2s ease;
`;

const RequirementIcon = styled.div<{ $met: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${({ $met, theme }) => $met ? theme.colors.success : theme.colors.gray300};
  color: white;
  transition: background 0.2s ease;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${({ theme }) => theme.spacing.lg};
`;

const ModalContent = styled.div`
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  max-width: 800px;
  width: 100%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: ${({ theme }) => theme.shadows.xl};
`;

const ModalHeader = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray200};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;
`;

const ModalCloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.gray100};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const ModalBody = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
  overflow-y: auto;
  line-height: 1.8;
  color: ${({ theme }) => theme.colors.textPrimary};

  h4 {
    font-size: ${({ theme }) => theme.typography.fontSize.lg};
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
    margin: ${({ theme }) => theme.spacing.lg} 0 ${({ theme }) => theme.spacing.md} 0;
    color: ${({ theme }) => theme.colors.primary};
  }

  p {
    margin: ${({ theme }) => theme.spacing.md} 0;
    text-align: justify;
  }

  ul, ol {
    margin: ${({ theme }) => theme.spacing.md} 0;
    padding-left: ${({ theme }) => theme.spacing.xl};
  }

  li {
    margin: ${({ theme }) => theme.spacing.xs} 0;
  }

  strong {
    font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const CheckboxWithLink = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md};
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px solid ${({ theme }) => theme.colors.gray200};
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  cursor: pointer;
  flex: 1;
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
  transition: color 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.primaryDark};
  }
`;

interface RegisterProps {
  userType: 'externo' | 'interno';
  onSwitchToLogin: () => void;
  onRegistrationComplete?: (userData: { username: string; password: string }) => void;
  onBack: () => void;
}

const Register: React.FC<RegisterProps> = ({ userType, onSwitchToLogin, onRegistrationComplete, onBack }) => {
  const [formData, setFormData] = useState({
    // Identificación básica
    proveedor_nombre: '',
    proveedor_nit: '',
    username: '',
    password: '',
    confirmPassword: '',
    
    // Contactos
    contacto1_nombre: '',
    contacto1_celular: '',
    contacto1_correo: '',
    contacto2_nombre: '',
    contacto2_celular: '',
    contacto2_correo: '',
    
    // Experiencia
    anios_experiencia: '',
    conocimiento_transporte_alimentos: false,
    ha_cargado_en_alianza: false,
    
    // Flota
    numero_vehiculos: '',
    porcentaje_flota_propia: '',
    edad_promedio_flota: '',
    
    // Tecnologías (arrays para SET)
    tecnologias: [] as string[],
    // Tecnologías con porcentaje de participación
    tecnologias_participacion: {
      'EURO III': '',
      'EURO IV': '',
      'EURO V': '',
      'EURO VI': '',
      'HIBRIDO': '',
      'GAS': '',
      'ELECTRICOS': '',
      'GASOLINA': ''
    } as Record<string, string>,
    capacidades_carga: [] as string[],
    tipos_vehiculo: [] as string[],
    // Tipos de vehículo con porcentaje de participación
    tipos_vehiculo_participacion: {
      'TRACTOMULA_3S3_TANQUE_ISOTERMICO': '',
      'TRACTOMULA_3S3_TANQUE_ACERADO': '',
      'TRACTOMULA_3S3_FURGON_EXTRADIMENSIONADO': '',
      'TRACTOMULA_3S3_FURGON_ESTANDAR': '',
      'TRACTOMULA_3S2_FURGON': '',
      'TRACTOMULA_3S2_FURGON_REFRIGERADO': '',
      'DOBLETROQUE_FURGON': '',
      'DOBLETROQUE_FURGON_REFRIGERADO': '',
      'SENCILLO_LIVIANO_PBV_9001_10500_FURGON': '',
      'VEHICULOS_LIVIANOS_FURGON': ''
    } as Record<string, string>,
    tipos_vehiculo_otro: '',
    
    // Documentos
    brochure_url: '',
    brochure_archivo: '',  // Nombre del archivo subido
    plan_descarbonizacion_existe: false,
    plan_descarbonizacion_url: '',
    plan_descarbonizacion_archivo: '',
    
    // Capacitación
    capacitacion_conductores: false,
    capacitacion_carga_alimentos: false,
    medicion_huella_carbono: false,
    
    // Plataforma
    gps_sistema: '',
    plataforma_monitoreo_features: [] as string[],
    plataforma_monitoreo_otros: '',
    reportes_automaticos_generador_carga: false,
    calculo_eta_seguimiento_tiempo_real: false,
    base_reportes_torre_control: false,
    base_pruebas_seguimiento_temperatura: false,
    plataforma_otros_especificar: '',
    
    // Nuevos campos de plataforma
    uso_peajes_electronicos: '',  // NO, HASTA_30, HASTA_60, HASTA_100
    base_soporte_prueba_entrega: false,
    cuenta_optimizador_ruta: false,
    
    // Certificaciones adicionales
    tiene_otra_certificacion: false,  // Pregunta binaria
    otra_certificacion: '',  // Descripción de la certificación
    soporte_otra_certificacion: '',  // URL o nombre de archivo
    anexar_soportes_url: '',
    
    // Puertos
    permisos_puertos: [] as string[],
    
    // Póliza
    poliza_limite_trayecto: '',
    monto_poliza_RCE: '',  // Monto póliza responsabilidad civil extracontractual
    empresa_contingencia_granel: '',
    capacidad_contingencia_granel: false,
    
    // Última milla
    capacidad_ultima_milla: false,
    ciudades_ultima_milla: [] as string[],
    
    // Rutas compensadas
    rutas_compensadas: false,
    rutas_compensadas_cuales: '',
    
    // Observaciones
    observaciones: '',
    
    // Aceptación de términos y tratamiento de datos
    acepta_aviso_privacidad: false,
    acepta_origen_fondos: false,
    acepta_codigo_proveedor: false,
    
    // Estado (se asigna automáticamente en backend, pero incluido para completitud)
    estado: 'BORRADOR'
  });

  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [planDescarbonizacionTipo, setPlanDescarbonizacionTipo] = useState<'url' | 'file'>('url');
  const [planDescarbonizacionFile, setPlanDescarbonizacionFile] = useState<File | null>(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [modalAbierto, setModalAbierto] = useState<'aviso' | 'fondos' | 'codigo' | null>(null);
  
  // Estado para manejar el tipo de adjunto del brochure
  const [brochureTipo, setBrochureTipo] = useState<'url' | 'file'>('url');
  const [brochureFile, setBrochureFile] = useState<File | null>(null);
  
  // Estado para manejar el tipo de adjunto de certificaciones
  const [otraCertificacionTipo, setOtraCertificacionTipo] = useState<'url' | 'file'>('url');
  const [otraCertificacionFile, setOtraCertificacionFile] = useState<File | null>(null);
  const { register } = useAuth();

  // Función para verificar requisitos de contraseña en tiempo real
  const checkPasswordRequirements = (password: string) => {
    return {
      length: password.length >= 12,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
    };
  };

  const passwordRequirements = checkPasswordRequirements(formData.password);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const target = e.target as HTMLInputElement;
      setFormData(prev => ({
        ...prev,
        [name]: target.checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleCheckboxArrayChange = (name: string, value: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      [name]: checked 
        ? [...(prev[name as keyof typeof prev] as string[]), value]
        : (prev[name as keyof typeof prev] as string[]).filter(item => item !== value)
    }));
  };

  // Manejar permisos de puertos con lógica de exclusión para "NINGUNO"
  // Si selecciona NINGUNO, deselecciona los demás. Si selecciona un puerto, deselecciona NINGUNO.
  const handlePuertosChange = (puerto: string, checked: boolean) => {
    setFormData(prev => {
      if (puerto === 'NINGUNO') {
        // Si selecciona NINGUNO, deseleccionar todos los demás
        return {
          ...prev,
          permisos_puertos: checked ? ['NINGUNO'] : []
        };
      } else {
        // Si selecciona un puerto específico, quitar NINGUNO de la lista
        const newPuertos = checked
          ? [...prev.permisos_puertos.filter(p => p !== 'NINGUNO'), puerto]
          : prev.permisos_puertos.filter(p => p !== puerto);
        return {
          ...prev,
          permisos_puertos: newPuertos
        };
      }
    });
  };

  // Manejar cambio de tecnología con porcentaje de participación
  // Permite deseleccionar si se hace clic en la opción ya seleccionada
  const handleTechParticipacionChange = (tech: string, participacion: string) => {
    setFormData(prev => {
      // Si el valor actual es igual al nuevo, lo deseleccionamos (toggle)
      const currentValue = prev.tecnologias_participacion[tech];
      const newValue = currentValue === participacion ? '' : participacion;
      
      return {
        ...prev,
        tecnologias_participacion: {
          ...prev.tecnologias_participacion,
          [tech]: newValue
        },
        // También actualizar el array de tecnologías para mantener compatibilidad
        tecnologias: newValue 
          ? [...prev.tecnologias.filter(t => t !== tech), tech]
          : prev.tecnologias.filter(t => t !== tech)
      };
    });
  };

  // Manejar cambio de tipo de vehículo con porcentaje de participación
  // Permite deseleccionar si se hace clic en la opción ya seleccionada
  const handleTipoVehiculoParticipacionChange = (tipo: string, participacion: string) => {
    setFormData(prev => {
      // Si el valor actual es igual al nuevo, lo deseleccionamos (toggle)
      const currentValue = prev.tipos_vehiculo_participacion[tipo];
      const newValue = currentValue === participacion ? '' : participacion;
      
      return {
        ...prev,
        tipos_vehiculo_participacion: {
          ...prev.tipos_vehiculo_participacion,
          [tipo]: newValue
        },
        // También actualizar el array de tipos_vehiculo para mantener compatibilidad
        tipos_vehiculo: newValue 
          ? [...prev.tipos_vehiculo.filter(t => t !== tipo), tipo]
          : prev.tipos_vehiculo.filter(t => t !== tipo)
      };
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPlanDescarbonizacionFile(file);
    }
  };

  // Handler para archivo del brochure
  const handleBrochureFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setBrochureFile(file);
    }
  };

  // Handler para archivo de certificaciones adicionales
  const handleCertificacionFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setOtraCertificacionFile(file);
    }
  };

  const uploadPlanDescarbonizacionFile = async (): Promise<string | null> => {
    if (!planDescarbonizacionFile) return null;

    try {
      setUploadingFile(true);
      
      // Necesitamos hacer login temporal para obtener un token
      // El registro aún no se ha hecho, así que usamos un endpoint que no requiere auth
      // O creamos un endpoint especial para subir archivos antes del registro
      
      // Por ahora, vamos a subir el archivo sin autenticación JWT pero con validación básica
      const formDataToSend = new FormData();
      formDataToSend.append('file', planDescarbonizacionFile);

      const response = await fetch(apiConfig.endpoints.uploadPlanDescarbonizacion, {
        method: 'POST',
        body: formDataToSend
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Error al subir el archivo');
      }

      // Retornar blob_name que incluye la ruta completa en GCS con timestamp
      // Esto permite descargar el archivo después
      return result.blob_name;
    } catch (error: any) {
      console.error('Error subiendo archivo:', error);
      throw error;
    } finally {
      setUploadingFile(false);
    }
  };

  // Función para subir archivo del brochure
  const uploadBrochureFile = async (): Promise<string | null> => {
    if (!brochureFile) return null;

    try {
      setUploadingFile(true);
      
      const formDataToSend = new FormData();
      formDataToSend.append('file', brochureFile);

      const response = await fetch(apiConfig.endpoints.uploadBrochure, {
        method: 'POST',
        body: formDataToSend
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Error al subir el archivo del brochure');
      }

      return result.blob_name;
    } catch (error: any) {
      console.error('Error subiendo archivo del brochure:', error);
      throw error;
    } finally {
      setUploadingFile(false);
    }
  };

  // Función para subir archivo de certificaciones adicionales
  const uploadCertificacionFile = async (): Promise<string | null> => {
    if (!otraCertificacionFile) return null;

    try {
      setUploadingFile(true);
      
      const formDataToSend = new FormData();
      formDataToSend.append('file', otraCertificacionFile);

      // Usamos el mismo endpoint público pero con un folder_prefix diferente
      const response = await fetch(apiConfig.endpoints.uploadOtraCertificacion, {
        method: 'POST',
        body: formDataToSend
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Error al subir el archivo de certificación');
      }

      return result.blob_name;
    } catch (error: any) {
      console.error('Error subiendo archivo de certificación:', error);
      throw error;
    } finally {
      setUploadingFile(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      // Validar campos requeridos
      if (!formData.proveedor_nombre || !formData.proveedor_nit || !formData.username) {
        throw new Error('Por favor complete todos los campos requeridos (*)');
      }

      // Validar correo de contacto principal
      if (!formData.contacto1_correo || !formData.contacto1_correo.trim()) {
        throw new Error('El correo del Contacto Principal es requerido');
      }

      // Validar contraseña
      if (!formData.password) {
        throw new Error('La contraseña es requerida');
      }

      if (formData.password.length < 12) {
        throw new Error('La contraseña debe tener al menos 12 caracteres');
      }

      if (!/[A-Z]/.test(formData.password)) {
        throw new Error('La contraseña debe contener al menos una letra mayúscula');
      }

      if (!/[a-z]/.test(formData.password)) {
        throw new Error('La contraseña debe contener al menos una letra minúscula');
      }

      if (!/[0-9]/.test(formData.password)) {
        throw new Error('La contraseña debe contener al menos un número');
      }

      if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(formData.password)) {
        throw new Error('La contraseña debe contener al menos un carácter especial');
      }

      if (formData.password !== formData.confirmPassword) {
        throw new Error('Las contraseñas no coinciden');
      }

      // Validar que se haya seleccionado años de experiencia
      if (!formData.anios_experiencia || formData.anios_experiencia === '') {
        throw new Error('Por favor seleccione los años de experiencia');
      }

      // Validar aceptación de documentos legales
      if (!formData.acepta_aviso_privacidad) {
        throw new Error('Debe aceptar el Aviso de Privacidad para continuar');
      }

      if (!formData.acepta_origen_fondos) {
        throw new Error('Debe aceptar la Declaración de Origen de Fondos para continuar');
      }

      if (!formData.acepta_codigo_proveedor) {
        throw new Error('Debe aceptar el Código de Proveedor y Contratista para continuar');
      }

      // Si se seleccionó archivo del brochure, subirlo ANTES del registro
      let uploadedBrochureFileName: string | null = null;
      if (brochureTipo === 'file' && brochureFile) {
        try {
          uploadedBrochureFileName = await uploadBrochureFile();
        } catch (uploadError) {
          console.error('Error al subir el archivo del brochure:', uploadError);
          throw new Error('Error al subir el archivo del brochure. Por favor, intente nuevamente.');
        }
      }

      // Si se seleccionó archivo del plan de descarbonización, subirlo
      let uploadedFileName: string | null = null;
      if (planDescarbonizacionTipo === 'file' && planDescarbonizacionFile) {
        try {
          uploadedFileName = await uploadPlanDescarbonizacionFile();
        } catch (uploadError) {
          console.error('Error al subir el archivo:', uploadError);
          throw new Error('Error al subir el archivo. Por favor, intente nuevamente.');
        }
      }

      // Si se seleccionó archivo de certificación, subirlo
      let uploadedCertificacionFileName: string | null = null;
      if (otraCertificacionTipo === 'file' && otraCertificacionFile) {
        try {
          uploadedCertificacionFileName = await uploadCertificacionFile();
        } catch (uploadError) {
          console.error('Error al subir el archivo de certificación:', uploadError);
          throw new Error('Error al subir el archivo de certificación. Por favor, intente nuevamente.');
        }
      }

      // Limpiar y preparar datos para envío
      const { confirmPassword, ...formDataWithoutConfirm } = formData;
      
      // Procesar tecnologías con porcentaje de participación
      // Formato: "EURO VI:HASTA_30,HIBRIDO:HASTA_60,GAS:HASTA_100"
      const tecnologiasConParticipacion = Object.entries(formData.tecnologias_participacion)
        .filter(([_, participacion]) => participacion !== '') // Solo incluir las que tienen valor
        .map(([tech, participacion]) => `${tech}:${participacion}`)
        .join(',');
      
      // Procesar tipos de vehículo con porcentaje de participación
      // Formato: "TRACTOMULA_3S3_TANQUE_ISOTERMICO:HASTA_30,DOBLETROQUE_FURGON:HASTA_60"
      const tiposVehiculoConParticipacion = Object.entries(formData.tipos_vehiculo_participacion)
        .filter(([_, participacion]) => participacion !== '') // Solo incluir los que tienen valor
        .map(([tipo, participacion]) => `${tipo}:${participacion}`)
        .join(',');
      
      // Agregar "OTRO" si se especificó
      const tiposVehiculoFinal = formData.tipos_vehiculo_otro 
        ? (tiposVehiculoConParticipacion ? `${tiposVehiculoConParticipacion},OTRO:${formData.tipos_vehiculo_otro}` : `OTRO:${formData.tipos_vehiculo_otro}`)
        : tiposVehiculoConParticipacion;
      
      const cleanedData = {
        ...formDataWithoutConfirm,
        // Sobrescribir tecnologías con el nuevo formato que incluye participación
        tecnologias: tecnologiasConParticipacion ? tecnologiasConParticipacion.split(',').map(t => t.split(':')[0]) : [],
        tecnologias_participacion_str: tecnologiasConParticipacion || null, // Campo adicional con formato completo
        // Sobrescribir tipos_vehiculo con el nuevo formato que incluye participación
        tipos_vehiculo: tiposVehiculoConParticipacion ? tiposVehiculoConParticipacion.split(',').map(t => t.split(':')[0]) : [],
        tipos_vehiculo_participacion_str: tiposVehiculoFinal || null, // Campo adicional con formato completo
        // Convertir campos numéricos vacíos a null
        numero_vehiculos: formData.numero_vehiculos ? parseInt(formData.numero_vehiculos) : null,
        poliza_limite_trayecto: formData.poliza_limite_trayecto ? parseFloat(formData.poliza_limite_trayecto) : null,
        monto_poliza_RCE: formData.monto_poliza_RCE ? parseFloat(formData.monto_poliza_RCE) : null,
        // Limpiar campos ENUM vacíos (convertir "" a null)
        anios_experiencia: formData.anios_experiencia || null,
        porcentaje_flota_propia: formData.porcentaje_flota_propia || null,
        edad_promedio_flota: formData.edad_promedio_flota || null,
        // Limpiar strings vacías
        contacto1_nombre: formData.contacto1_nombre.trim() || null,
        contacto1_celular: formData.contacto1_celular.trim() || null,
        contacto1_correo: formData.contacto1_correo.trim() || null,
        contacto2_nombre: formData.contacto2_nombre.trim() || null,
        contacto2_celular: formData.contacto2_celular.trim() || null,
        contacto2_correo: formData.contacto2_correo.trim() || null,
        tipos_vehiculo_otro: formData.tipos_vehiculo_otro.trim() || null,
        // Si se subió archivo del brochure, usar el nombre del archivo; si no, usar la URL proporcionada
        brochure_url: uploadedBrochureFileName || formData.brochure_url.trim() || null,
        brochure_archivo: uploadedBrochureFileName, // Nombre del archivo subido
        plan_descarbonizacion_url: formData.plan_descarbonizacion_url.trim() || null,
        plan_descarbonizacion_archivo: uploadedFileName, // Incluir el nombre del archivo subido
        gps_sistema: formData.gps_sistema.trim() || null,
        plataforma_monitoreo_otros: formData.plataforma_monitoreo_otros.trim() || null,
        plataforma_otros_especificar: formData.plataforma_otros_especificar.trim() || null,
        // Certificaciones adicionales
        tiene_otra_certificacion: formData.tiene_otra_certificacion,
        otra_certificacion: formData.otra_certificacion.trim() || null,
        // Si se subió archivo, usar el nombre del archivo; si no, usar la URL proporcionada
        soporte_otra_certificacion: uploadedCertificacionFileName || formData.soporte_otra_certificacion.trim() || null,
        anexar_soportes_url: formData.anexar_soportes_url.trim() || null,
        empresa_contingencia_granel: formData.empresa_contingencia_granel.trim() || null,
        rutas_compensadas_cuales: formData.rutas_compensadas_cuales.trim() || null,
        observaciones: formData.observaciones.trim() || null,
        // El estado se maneja automáticamente en el backend como 'BORRADOR'
        estado: 'BORRADOR'
      };

      // Registrar el proveedor con el nombre del archivo ya incluido
      await register(cleanedData);
      
      // Llamar callback de registro completado para mostrar formulario de sostenibilidad
      if (onRegistrationComplete) {
        onRegistrationComplete({
          username: formData.username,
          password: formData.password
        });
      }
    } catch (error: any) {
      let errorMessage = error.message || 'Error al registrar el proveedor';
      
      // Personalizar mensajes de error comunes
      if (errorMessage.includes('Ya existe un proveedor')) {
        errorMessage = 'Ya existe un proveedor registrado con ese NIT o nombre de usuario. Por favor, use datos diferentes o inicie sesión si ya tiene una cuenta.';
        
        // Limpiar campos sensibles para facilitar la corrección
        setFormData(prev => ({
          ...prev,
          proveedor_nit: '',
          username: ''
        }));
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <RegisterContainer>
      <RegisterCard>
        <Header>
          <LogoIcon>
            <UserPlus size={40} color="white" />
          </LogoIcon>
          <Title>Registro de Proveedor</Title>
          <Subtitle>Complete la información para registrarse en el sistema</Subtitle>
        </Header>

        {error && (
          <Message type="error">
            <AlertCircle size={16} />
            {error}
          </Message>
        )}

        <Form onSubmit={handleSubmit}>
          {/* Información Básica */}
          <Section>
            <SectionTitle>
              <Building size={20} />
              Información Básica
            </SectionTitle>
            <FormGrid>
              <InputGroup>
                <Label>Nombre del Proveedor <RequiredMark>*</RequiredMark></Label>
                <Input
                  type="text"
                  name="proveedor_nombre"
                  value={formData.proveedor_nombre}
                  onChange={handleChange}
                  placeholder="Ej: Transportes ABC S.A.S."
                  required
                />
              </InputGroup>
              <InputGroup>
                <Label>NIT <RequiredMark>*</RequiredMark></Label>
                <Input
                  type="text"
                  name="proveedor_nit"
                  value={formData.proveedor_nit}
                  onChange={handleChange}
                  placeholder="Ej: 900123456-1"
                  required
                />
              </InputGroup>
              <InputGroup>
                <Label>Nombre de Usuario <RequiredMark>*</RequiredMark></Label>
                <Input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  placeholder="Ej: transportesabc"
                  required
                />
              </InputGroup>
            </FormGrid>
          </Section>

          {/* Sección de Seguridad - Contraseña */}
          <Section>
            <SectionTitle>
              <Lock size={20} />
              Seguridad de la Cuenta
            </SectionTitle>
            <FormGrid>
              <InputGroup $fullWidth>
                <Label>Contraseña <RequiredMark>*</RequiredMark></Label>
                <Input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Cree una contraseña segura"
                  required
                />
              </InputGroup>

              {/* Mostrar requisitos de contraseña cuando el usuario empieza a escribir */}
              {formData.password && (
                <InputGroup $fullWidth>
                  <PasswordRequirements>
                    <RequirementTitle>Requisitos de contraseña:</RequirementTitle>
                    <RequirementsList>
                      <RequirementItem $met={passwordRequirements.length}>
                        <RequirementIcon $met={passwordRequirements.length}>
                          {passwordRequirements.length ? <Check size={10} /> : <X size={10} />}
                        </RequirementIcon>
                        Mínimo 12 caracteres
                      </RequirementItem>
                      <RequirementItem $met={passwordRequirements.uppercase}>
                        <RequirementIcon $met={passwordRequirements.uppercase}>
                          {passwordRequirements.uppercase ? <Check size={10} /> : <X size={10} />}
                        </RequirementIcon>
                        Al menos una letra mayúscula (A-Z)
                      </RequirementItem>
                      <RequirementItem $met={passwordRequirements.lowercase}>
                        <RequirementIcon $met={passwordRequirements.lowercase}>
                          {passwordRequirements.lowercase ? <Check size={10} /> : <X size={10} />}
                        </RequirementIcon>
                        Al menos una letra minúscula (a-z)
                      </RequirementItem>
                      <RequirementItem $met={passwordRequirements.number}>
                        <RequirementIcon $met={passwordRequirements.number}>
                          {passwordRequirements.number ? <Check size={10} /> : <X size={10} />}
                        </RequirementIcon>
                        Al menos un número (0-9)
                      </RequirementItem>
                      <RequirementItem $met={passwordRequirements.special}>
                        <RequirementIcon $met={passwordRequirements.special}>
                          {passwordRequirements.special ? <Check size={10} /> : <X size={10} />}
                        </RequirementIcon>
                        Al menos un carácter especial (!@#$%^&*...)
                      </RequirementItem>
                    </RequirementsList>
                  </PasswordRequirements>
                </InputGroup>
              )}

              <InputGroup $fullWidth>
                <Label>Confirmar Contraseña <RequiredMark>*</RequiredMark></Label>
                <Input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Vuelva a ingresar su contraseña"
                  required
                />
              </InputGroup>
            </FormGrid>
          </Section>

          {/* Contactos */}
          <Section>
            <SectionTitle>
              <User size={20} />
              Información de Contacto
            </SectionTitle>
            <FormGrid>
              <InputGroup>
                <Label>Contacto Principal - Nombre</Label>
                <Input
                  type="text"
                  name="contacto1_nombre"
                  value={formData.contacto1_nombre}
                  onChange={handleChange}
                  placeholder="Ej: Juan Pérez"
                />
              </InputGroup>
              <InputGroup>
                <Label>Contacto Principal - Celular</Label>
                <Input
                  type="tel"
                  name="contacto1_celular"
                  value={formData.contacto1_celular}
                  onChange={handleChange}
                  placeholder="Ej: +57 300 123 4567"
                />
              </InputGroup>
              <InputGroup>
                <Label>Contacto Principal - Correo <RequiredMark>*</RequiredMark></Label>
                <Input
                  type="email"
                  name="contacto1_correo"
                  value={formData.contacto1_correo}
                  onChange={handleChange}
                  placeholder="Ej: juan.perez@empresa.com"
                  required
                />
              </InputGroup>
              <InputGroup>
                <Label>Contacto Secundario - Nombre</Label>
                <Input
                  type="text"
                  name="contacto2_nombre"
                  value={formData.contacto2_nombre}
                  onChange={handleChange}
                  placeholder="Ej: María García"
                />
              </InputGroup>
              <InputGroup>
                <Label>Contacto Secundario - Celular</Label>
                <Input
                  type="tel"
                  name="contacto2_celular"
                  value={formData.contacto2_celular}
                  onChange={handleChange}
                  placeholder="Ej: +57 301 987 6543"
                />
              </InputGroup>
              <InputGroup>
                <Label>Contacto Secundario - Correo</Label>
                <Input
                  type="email"
                  name="contacto2_correo"
                  value={formData.contacto2_correo}
                  onChange={handleChange}
                  placeholder="Ej: maria.garcia@empresa.com"
                />
              </InputGroup>
            </FormGrid>
          </Section>

          {/* Experiencia */}
          <Section>
            <SectionTitle>
              <Shield size={20} />
              Experiencia y Relación
            </SectionTitle>
            <FormGrid>
              <InputGroup>
                <Label>Años de Experiencia <RequiredMark>*</RequiredMark></Label>
                <Select
                  name="anios_experiencia"
                  value={formData.anios_experiencia}
                  onChange={handleChange}
                  required
                >
                  <option value="">Seleccione...</option>
                  <option value="1 A 3 AÑOS">1 a 3 años</option>
                  <option value="4 A 10 AÑOS">4 a 10 años</option>
                  <option value="MÁS DE 10 AÑOS">Más de 10 años</option>
                </Select>
              </InputGroup>
              <InputGroup>
                <CheckboxItem>
                  <Checkbox
                    name="conocimiento_transporte_alimentos"
                    checked={formData.conocimiento_transporte_alimentos}
                    onChange={handleChange}
                  />
                  Conocimiento en transporte de alimentos
                </CheckboxItem>
              </InputGroup>
              <InputGroup>
                <CheckboxItem>
                  <Checkbox
                    name="ha_cargado_en_alianza"
                    checked={formData.ha_cargado_en_alianza}
                    onChange={handleChange}
                  />
                  Ha cargado anteriormente en Alianza Team
                </CheckboxItem>
              </InputGroup>
            </FormGrid>
          </Section>

          {/* Capacitación y Certificaciones */}
          <Section>
            <SectionTitle>
              <Shield size={20} />
              Capacitación y Certificaciones
            </SectionTitle>
            <FormGrid>
              <InputGroup>
                <CheckboxItem>
                  <Checkbox
                    name="capacitacion_conductores"
                    checked={formData.capacitacion_conductores}
                    onChange={handleChange}
                  />
                  ¿Cuenta con capacitaciones y formación a conductores en manejo seguro y/o conducción amigable?
                </CheckboxItem>
              </InputGroup>
              <InputGroup>
                <CheckboxItem>
                  <Checkbox
                    name="capacitacion_carga_alimentos"
                    checked={formData.capacitacion_carga_alimentos}
                    onChange={handleChange}
                  />
                  ¿Cuenta con capacitaciones para la carga de alimentos?
                </CheckboxItem>
              </InputGroup>
              <InputGroup>
                <CheckboxItem>
                  <Checkbox
                    name="medicion_huella_carbono"
                    checked={formData.medicion_huella_carbono}
                    onChange={handleChange}
                  />
                  ¿Actualmente cuenta con medición de la huella de carbono?
                </CheckboxItem>
              </InputGroup>
            </FormGrid>
          </Section>

          {/* Flota */}
          <Section>
            <SectionTitle>
              <Truck size={20} />
              Información de Flota
            </SectionTitle>
            <FormGrid>
              <InputGroup>
                <Label>Número de Vehículos</Label>
                <Input
                  type="number"
                  name="numero_vehiculos"
                  value={formData.numero_vehiculos}
                  onChange={handleChange}
                  placeholder="Ej: 25"
                  min="0"
                />
              </InputGroup>
              <InputGroup>
                <Label>Porcentaje de Flota Propia</Label>
                <Select
                  name="porcentaje_flota_propia"
                  value={formData.porcentaje_flota_propia}
                  onChange={handleChange}
                >
                  <option value="">Seleccione...</option>
                  <option value="1% AL 10%">1% al 10%</option>
                  <option value="10% AL 30%">10% al 30%</option>
                  <option value="MÁS DEL 30%">Más del 30%</option>
                </Select>
              </InputGroup>
              <InputGroup>
                <Label>Edad Promedio de la Flota</Label>
                <Select
                  name="edad_promedio_flota"
                  value={formData.edad_promedio_flota}
                  onChange={handleChange}
                >
                  <option value="">Seleccione...</option>
                  <option value="INFERIOR A 3 AÑOS">Inferior a 3 años</option>
                  <option value="3 A 10 AÑOS">3 a 10 años</option>
                  <option value="MÁS DE 10 AÑOS">Más de 10 años</option>
                </Select>
              </InputGroup>
            </FormGrid>

            {/* Tecnologías con % de Participación */}
            <InputGroup $fullWidth>
              <Label>Tecnologías de Vehículos - % de Participación (seleccione el porcentaje que aplique para cada tecnología)</Label>
              <TechTable>
                <TechTableHeader>
                  <tr>
                    <TechTableHeaderCell style={{ width: '40%' }}>TECNOLOGÍA</TechTableHeaderCell>
                    <TechTableHeaderCell colSpan={3}>% PARTICIPACIÓN</TechTableHeaderCell>
                  </tr>
                  <tr>
                    <TechTableHeaderCell></TechTableHeaderCell>
                    <TechTableHeaderCell>HASTA 30%</TechTableHeaderCell>
                    <TechTableHeaderCell>HASTA 60%</TechTableHeaderCell>
                    <TechTableHeaderCell>HASTA 100%</TechTableHeaderCell>
                  </tr>
                </TechTableHeader>
                <TechTableBody>
                  {['EURO III', 'EURO IV', 'EURO V', 'EURO VI', 'HIBRIDO', 'GAS', 'ELECTRICOS', 'GASOLINA'].map(tech => (
                    <TechTableRow key={tech}>
                      <TechNameCell>{tech}</TechNameCell>
                      <TechTableCell>
                        <RadioInput
                          name={`tech_${tech.replace(/\s/g, '_')}`}
                          value="HASTA_30"
                          checked={formData.tecnologias_participacion[tech] === 'HASTA_30'}
                          onClick={() => handleTechParticipacionChange(tech, 'HASTA_30')}
                          onChange={() => {}} // Necesario para evitar warning de React
                        />
                      </TechTableCell>
                      <TechTableCell>
                        <RadioInput
                          name={`tech_${tech.replace(/\s/g, '_')}`}
                          value="HASTA_60"
                          checked={formData.tecnologias_participacion[tech] === 'HASTA_60'}
                          onClick={() => handleTechParticipacionChange(tech, 'HASTA_60')}
                          onChange={() => {}} // Necesario para evitar warning de React
                        />
                      </TechTableCell>
                      <TechTableCell>
                        <RadioInput
                          name={`tech_${tech.replace(/\s/g, '_')}`}
                          value="HASTA_100"
                          checked={formData.tecnologias_participacion[tech] === 'HASTA_100'}
                          onClick={() => handleTechParticipacionChange(tech, 'HASTA_100')}
                          onChange={() => {}} // Necesario para evitar warning de React
                        />
                      </TechTableCell>
                    </TechTableRow>
                  ))}
                </TechTableBody>
              </TechTable>
            </InputGroup>

            {/* Capacidades de Carga */}
            <InputGroup $fullWidth>
              <Label>Capacidades de Carga (seleccione todas las que apliquen)</Label>
              <CheckboxGroup>
                {[
                  { value: 'LIQUIDOS_GRANEL_TANQUES', label: 'Líquidos a Granel (Tanques Isotérmicos - Acerados)' },
                  { value: 'SECO_FURGON_CONTENEDOR', label: 'Seco en Vehículos Furgón/Contenedor' },
                  { value: 'REFRIGERADO', label: 'Refrigerado' },
                  { value: 'CONGELADO', label: 'Congelado' }
                ].map(cap => (
                  <CheckboxItem key={cap.value}>
                    <Checkbox
                      checked={formData.capacidades_carga.includes(cap.value)}
                      onChange={(e) => handleCheckboxArrayChange('capacidades_carga', cap.value, e.target.checked)}
                    />
                    {cap.label}
                  </CheckboxItem>
                ))}
              </CheckboxGroup>
            </InputGroup>

            {/* Tipos de Vehículos con % de Participación */}
            <InputGroup $fullWidth>
              <Label>Tipos de Vehículos Disponibles - % de Participación (seleccione el porcentaje que aplique)</Label>
              <TechTable>
                <TechTableHeader>
                  <tr>
                    <TechTableHeaderCell style={{ width: '50%' }}>TIPO DE FLOTA</TechTableHeaderCell>
                    <TechTableHeaderCell colSpan={3}>% PARTICIPACIÓN</TechTableHeaderCell>
                  </tr>
                  <tr>
                    <TechTableHeaderCell></TechTableHeaderCell>
                    <TechTableHeaderCell>HASTA 30%</TechTableHeaderCell>
                    <TechTableHeaderCell>HASTA 60%</TechTableHeaderCell>
                    <TechTableHeaderCell>HASTA 100%</TechTableHeaderCell>
                  </tr>
                </TechTableHeader>
                <TechTableBody>
                  {[
                    { value: 'TRACTOMULA_3S3_TANQUE_ISOTERMICO', label: 'Tractomula 3S3 Tanque Isotérmico' },
                    { value: 'TRACTOMULA_3S3_TANQUE_ACERADO', label: 'Tractomula 3S3 Tanque Acerado' },
                    { value: 'TRACTOMULA_3S3_FURGON_EXTRADIMENSIONADO', label: 'Tractomula 3S3 Furgón Extradimensionado' },
                    { value: 'TRACTOMULA_3S3_FURGON_ESTANDAR', label: 'Tractomula 3S3 Furgón Estándar' },
                    { value: 'TRACTOMULA_3S2_FURGON', label: 'Tractomula 3S2 Furgón' },
                    { value: 'TRACTOMULA_3S2_FURGON_REFRIGERADO', label: 'Tractomula 3S2 Furgón Refrigerado' },
                    { value: 'DOBLETROQUE_FURGON', label: 'Dobletroque Furgón' },
                    { value: 'DOBLETROQUE_FURGON_REFRIGERADO', label: 'Dobletroque Furgón Refrigerado' },
                    { value: 'SENCILLO_LIVIANO_PBV_9001_10500_FURGON', label: 'Sencillo Liviano PBV 9001 - 10500 KG - Furgón' },
                    { value: 'VEHICULOS_LIVIANOS_FURGON', label: 'Vehículos Livianos - Furgón' }
                  ].map(tipo => (
                    <TechTableRow key={tipo.value}>
                      <TechNameCell>{tipo.label}</TechNameCell>
                      <TechTableCell>
                        <RadioInput
                          name={`tipo_${tipo.value}`}
                          value="HASTA_30"
                          checked={formData.tipos_vehiculo_participacion[tipo.value] === 'HASTA_30'}
                          onClick={() => handleTipoVehiculoParticipacionChange(tipo.value, 'HASTA_30')}
                          onChange={() => {}} // Necesario para evitar warning de React
                        />
                      </TechTableCell>
                      <TechTableCell>
                        <RadioInput
                          name={`tipo_${tipo.value}`}
                          value="HASTA_60"
                          checked={formData.tipos_vehiculo_participacion[tipo.value] === 'HASTA_60'}
                          onClick={() => handleTipoVehiculoParticipacionChange(tipo.value, 'HASTA_60')}
                          onChange={() => {}} // Necesario para evitar warning de React
                        />
                      </TechTableCell>
                      <TechTableCell>
                        <RadioInput
                          name={`tipo_${tipo.value}`}
                          value="HASTA_100"
                          checked={formData.tipos_vehiculo_participacion[tipo.value] === 'HASTA_100'}
                          onClick={() => handleTipoVehiculoParticipacionChange(tipo.value, 'HASTA_100')}
                          onChange={() => {}} // Necesario para evitar warning de React
                        />
                      </TechTableCell>
                    </TechTableRow>
                  ))}
                </TechTableBody>
              </TechTable>
            </InputGroup>

            {/* Campo adicional para "Otro" tipo de vehículo */}
            <InputGroup $fullWidth>
              <Label>Otro tipo de vehículo (especifique si aplica)</Label>
              <Input
                type="text"
                name="tipos_vehiculo_otro"
                value={formData.tipos_vehiculo_otro}
                onChange={handleChange}
                placeholder="Especifique otro tipo de vehículo..."
              />
            </InputGroup>
          </Section>

          {/* Documentos y Adjuntos */}
          <Section>
            <SectionTitle>
              <FileText size={20} />
              Documentos y Adjuntos
            </SectionTitle>
            <FormGrid>
              <InputGroup $fullWidth>
                <Label>Brochure de la Empresa</Label>
                <RadioGroup style={{ marginBottom: '1rem' }}>
                  <RadioItem>
                    <input
                      type="radio"
                      name="brochureTipo"
                      value="url"
                      checked={brochureTipo === 'url'}
                      onChange={() => setBrochureTipo('url')}
                    />
                    URL
                  </RadioItem>
                  <RadioItem>
                    <input
                      type="radio"
                      name="brochureTipo"
                      value="file"
                      checked={brochureTipo === 'file'}
                      onChange={() => setBrochureTipo('file')}
                    />
                    Subir Archivo
                  </RadioItem>
                </RadioGroup>

                {brochureTipo === 'url' ? (
                  <Input
                    type="url"
                    name="brochure_url"
                    value={formData.brochure_url}
                    onChange={handleChange}
                    placeholder="https://ejemplo.com/brochure.pdf"
                  />
                ) : (
                  <FileInputWrapper>
                    <FileInputLabel htmlFor="brochure-file">
                      {brochureFile 
                        ? '✓ Archivo seleccionado - Click para cambiar' 
                        : '📁 Click para seleccionar archivo (PDF, DOC, DOCX, JPG, PNG)'}
                    </FileInputLabel>
                    <FileInputHidden
                      id="brochure-file"
                      type="file"
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      onChange={handleBrochureFileChange}
                    />
                    {brochureFile && (
                      <FileName>
                        📄 {brochureFile.name}
                      </FileName>
                    )}
                  </FileInputWrapper>
                )}
              </InputGroup>
              <InputGroup>
                <CheckboxItem>
                  <Checkbox
                    name="plan_descarbonizacion_existe"
                    checked={formData.plan_descarbonizacion_existe}
                    onChange={handleChange}
                  />
                  ¿Existe un plan de descarbonización?
                </CheckboxItem>
              </InputGroup>
            </FormGrid>
          </Section>

          {/* Plan de Descarbonización y Plataforma de Monitoreo */}
          <Section>
            <SectionTitle>
              <Settings size={20} />
              Plan de Descarbonización y Plataforma de Monitoreo
            </SectionTitle>
            <FormGrid>
              <InputGroup $fullWidth>
                <Label>Adjuntar Plan de Descarbonización</Label>
                <RadioGroup style={{ marginBottom: '1rem' }}>
                  <RadioItem>
                    <input
                      type="radio"
                      name="planDescarbonizacionTipo"
                      value="url"
                      checked={planDescarbonizacionTipo === 'url'}
                      onChange={(e) => setPlanDescarbonizacionTipo('url')}
                    />
                    URL
                  </RadioItem>
                  <RadioItem>
                    <input
                      type="radio"
                      name="planDescarbonizacionTipo"
                      value="file"
                      checked={planDescarbonizacionTipo === 'file'}
                      onChange={(e) => setPlanDescarbonizacionTipo('file')}
                    />
                    Subir Archivo
                  </RadioItem>
                </RadioGroup>

                {planDescarbonizacionTipo === 'url' ? (
                  <Input
                    type="url"
                    name="plan_descarbonizacion_url"
                    value={formData.plan_descarbonizacion_url}
                    onChange={handleChange}
                    placeholder="https://ejemplo.com/plan-descarbonizacion.pdf"
                  />
                ) : (
                  <FileInputWrapper>
                    <FileInputLabel htmlFor="plan-descarbonizacion-file">
                      {planDescarbonizacionFile 
                        ? '✓ Archivo seleccionado - Click para cambiar' 
                        : '📁 Click para seleccionar archivo'}
                    </FileInputLabel>
                    <FileInputHidden
                      id="plan-descarbonizacion-file"
                      type="file"
                      accept=".pdf,.doc,.docx,.xls,.xlsx"
                      onChange={handleFileChange}
                    />
                    {planDescarbonizacionFile && (
                      <FileName>
                        📄 {planDescarbonizacionFile.name}
                      </FileName>
                    )}
                  </FileInputWrapper>
                )}
              </InputGroup>
              <InputGroup>
                <CheckboxItem>
                  <Checkbox
                    name="reportes_automaticos_generador_carga"
                    checked={formData.reportes_automaticos_generador_carga}
                    onChange={handleChange}
                  />
                  ¿La plataforma de monitoreo reportes automáticos generador de carga?
                </CheckboxItem>
              </InputGroup>
              <InputGroup>
                <CheckboxItem>
                  <Checkbox
                    name="calculo_eta_seguimiento_tiempo_real"
                    checked={formData.calculo_eta_seguimiento_tiempo_real}
                    onChange={handleChange}
                  />
                  ¿Cálculo ETA del vehículo seguimiento en tiempo real?
                </CheckboxItem>
              </InputGroup>
              <InputGroup>
                <CheckboxItem>
                  <Checkbox
                    name="base_reportes_torre_control"
                    checked={formData.base_reportes_torre_control}
                    onChange={handleChange}
                  />
                  ¿Base de reportes en la torre de control?
                </CheckboxItem>
              </InputGroup>
              <InputGroup>
                <CheckboxItem>
                  <Checkbox
                    name="base_pruebas_seguimiento_temperatura"
                    checked={formData.base_pruebas_seguimiento_temperatura}
                    onChange={handleChange}
                  />
                  ¿Base de pruebas seguimiento de temperatura del furgón?
                </CheckboxItem>
              </InputGroup>
              <InputGroup $fullWidth>
                <Label>Otros, ¿cuál?</Label>
                <Input
                  type="text"
                  name="plataforma_otros_especificar"
                  value={formData.plataforma_otros_especificar}
                  onChange={handleChange}
                  placeholder="Especifique otras características de la plataforma"
                />
              </InputGroup>

              {/* Uso de peajes electrónicos */}
              <InputGroup $fullWidth>
                <Label>La empresa tiene implementado el uso de peajes electrónicos en la totalidad de la flota</Label>
                <Select
                  name="uso_peajes_electronicos"
                  value={formData.uso_peajes_electronicos}
                  onChange={handleChange}
                >
                  <option value="">Seleccione...</option>
                  <option value="NO">No</option>
                  <option value="HASTA_30">Hasta el 30% de la flota</option>
                  <option value="HASTA_60">Hasta el 60% de la flota</option>
                  <option value="HASTA_100">Hasta el 100% de la flota</option>
                </Select>
              </InputGroup>

              {/* Nuevas preguntas de plataforma */}
              <InputGroup>
                <CheckboxItem>
                  <Checkbox
                    name="base_soporte_prueba_entrega"
                    checked={formData.base_soporte_prueba_entrega}
                    onChange={handleChange}
                  />
                  ¿Cuenta con base para cargar el soporte magnético de la prueba de entrega?
                </CheckboxItem>
              </InputGroup>
              <InputGroup>
                <CheckboxItem>
                  <Checkbox
                    name="cuenta_optimizador_ruta"
                    checked={formData.cuenta_optimizador_ruta}
                    onChange={handleChange}
                  />
                  ¿Cuenta con optimizador de ruta?
                </CheckboxItem>
              </InputGroup>

              {/* Nueva sección: Certificaciones adicionales - Pregunta binaria */}
              <InputGroup $fullWidth>
                <CheckboxItem>
                  <Checkbox
                    name="tiene_otra_certificacion"
                    checked={formData.tiene_otra_certificacion}
                    onChange={handleChange}
                  />
                  ¿La empresa de transportes cuenta con algún tipo de certificación diferente a los establecidos por los entes regulatorios (ISO, BASC, OEA, etc.)?
                </CheckboxItem>
              </InputGroup>

              {/* Mostrar campo de adjunto solo si tiene certificación adicional */}
              {formData.tiene_otra_certificacion && (
                <>
                  <InputGroup $fullWidth>
                    <Label>Especifique la(s) certificación(es)</Label>
                    <Input
                      type="text"
                      name="otra_certificacion"
                      value={formData.otra_certificacion}
                      onChange={handleChange}
                      placeholder="Ej: Certificación XYZ, Norma ABC..."
                    />
                  </InputGroup>

                  <InputGroup $fullWidth>
                    <Label>Adjuntar soporte de certificación</Label>
                    <RadioGroup style={{ marginBottom: '1rem' }}>
                      <RadioItem>
                        <input
                          type="radio"
                          name="otraCertificacionTipo"
                          value="url"
                          checked={otraCertificacionTipo === 'url'}
                          onChange={() => setOtraCertificacionTipo('url')}
                        />
                        URL
                      </RadioItem>
                      <RadioItem>
                        <input
                          type="radio"
                          name="otraCertificacionTipo"
                          value="file"
                          checked={otraCertificacionTipo === 'file'}
                          onChange={() => setOtraCertificacionTipo('file')}
                        />
                        Subir Archivo
                      </RadioItem>
                    </RadioGroup>

                    {otraCertificacionTipo === 'url' ? (
                      <Input
                        type="url"
                        name="soporte_otra_certificacion"
                        value={formData.soporte_otra_certificacion}
                        onChange={handleChange}
                        placeholder="https://ejemplo.com/certificacion.pdf"
                      />
                    ) : (
                      <FileInputWrapper>
                        <FileInputLabel htmlFor="otra-certificacion-file">
                          {otraCertificacionFile 
                            ? '✓ Archivo seleccionado - Click para cambiar' 
                            : '📁 Click para seleccionar archivo (PDF, DOC, DOCX)'}
                        </FileInputLabel>
                        <FileInputHidden
                          id="otra-certificacion-file"
                          type="file"
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          onChange={handleCertificacionFileChange}
                        />
                        {otraCertificacionFile && (
                          <FileName>
                            📄 {otraCertificacionFile.name}
                          </FileName>
                        )}
                      </FileInputWrapper>
                    )}
                  </InputGroup>
                </>
              )}
            </FormGrid>
          </Section>

          {/* Permisos para Puertos */}
          <Section>
            <SectionTitle>
              <Globe size={20} />
              Permisos para Puertos
            </SectionTitle>
            <FormGrid>
              <InputGroup $fullWidth>
                <Label>¿Cuenta con permisos para ingresar a puertos? (seleccione todos los que apliquen)</Label>
                <CheckboxGroup>
                  {[
                    'CARTAGENA',
                    'BARRANQUILLA', 
                    'SANTA MARTA',
                    'BUENAVENTURA',
                    'NINGUNO'
                  ].map(puerto => (
                    <CheckboxItem key={puerto}>
                      <Checkbox
                        checked={formData.permisos_puertos.includes(puerto)}
                        onChange={(e) => handlePuertosChange(puerto, e.target.checked)}
                      />
                      {puerto}
                    </CheckboxItem>
                  ))}
                </CheckboxGroup>
              </InputGroup>
              <InputGroup $fullWidth>
                <Label>Anexar Soportes</Label>
                <Input
                  type="url"
                  name="anexar_soportes_url"
                  value={formData.anexar_soportes_url}
                  onChange={handleChange}
                  placeholder="URL de los soportes de permisos"
                />
              </InputGroup>
            </FormGrid>
          </Section>

          {/* Póliza y Contingencia */}
          <Section>
            <SectionTitle>
              <Shield size={20} />
              Póliza y Contingencia
            </SectionTitle>
            <FormGrid>
              <InputGroup>
                <Label>De acuerdo a la póliza de transporte, ¿cuál es el límite por trayecto? (COP)</Label>
                <Input
                  type="number"
                  name="poliza_limite_trayecto"
                  value={formData.poliza_limite_trayecto}
                  onChange={handleChange}
                  placeholder="Ej: 1000000000"
                  step="0.01"
                />
              </InputGroup>
              <InputGroup>
                <Label>¿Cuál es el monto de la póliza de responsabilidad civil extracontractual? (COP)</Label>
                <Input
                  type="number"
                  name="monto_poliza_RCE"
                  value={formData.monto_poliza_RCE}
                  onChange={handleChange}
                  placeholder="Ej: 500000000"
                  step="0.01"
                />
              </InputGroup>
              <InputGroup>
                <Label>Empresa de Contingencia para Granel</Label>
                <Input
                  type="text"
                  name="empresa_contingencia_granel"
                  value={formData.empresa_contingencia_granel}
                  onChange={handleChange}
                  placeholder="Ej: Contingencia ABC S.A.S."
                />
              </InputGroup>
              <InputGroup $fullWidth>
                <CheckboxItem>
                  <Checkbox
                    name="capacidad_contingencia_granel"
                    checked={formData.capacidad_contingencia_granel}
                    onChange={handleChange}
                  />
                  Si tiene capacidad para transporte a granel, ¿tiene capacidad para atender contingencias?
                </CheckboxItem>
              </InputGroup>
            </FormGrid>
          </Section>

          {/* Capacidades de Última Milla */}
          <Section>
            <SectionTitle>
              <Truck size={20} />
              Capacidades de Última Milla
            </SectionTitle>
            <FormGrid>
              <InputGroup $fullWidth>
                <CheckboxItem>
                  <Checkbox
                    name="capacidad_ultima_milla"
                    checked={formData.capacidad_ultima_milla}
                    onChange={handleChange}
                  />
                  ¿Tiene capacidad para activar la última milla?
                </CheckboxItem>
              </InputGroup>
              <InputGroup $fullWidth>
                <Label>En cuáles de las siguientes ciudades tiene capacidad para activar la última milla? (seleccione todas las que apliquen)</Label>
                <CheckboxGroup>
                  {[
                    'BOGOTA_Y_ALREDEDORES',
                    'MEDELLIN_Y_ALREDEDORES',
                    'CALI_Y_ALREDEDORES',
                    'BARRANQUILLA_Y_ALREDEDORES',
                    'BUCARAMANGA_Y_ALREDEDORES',
                    'PEREIRA_Y_ALREDEDORES',
                    'IBAGUE_Y_ALREDEDORES'
                  ].map(ciudad => (
                    <CheckboxItem key={ciudad}>
                      <Checkbox
                        checked={formData.ciudades_ultima_milla.includes(ciudad)}
                        onChange={(e) => handleCheckboxArrayChange('ciudades_ultima_milla', ciudad, e.target.checked)}
                      />
                      {ciudad.replace(/_Y_ALREDEDORES/g, ' Y ALREDEDORES').replace(/_/g, ' ')}
                    </CheckboxItem>
                  ))}
                </CheckboxGroup>
              </InputGroup>
            </FormGrid>
          </Section>

          {/* Rutas Compensadas */}
          <Section>
            <SectionTitle>
              <Globe size={20} />
              Rutas Compensadas
            </SectionTitle>
            <FormGrid>
              <InputGroup>
                <CheckboxItem>
                  <Checkbox
                    name="rutas_compensadas"
                    checked={formData.rutas_compensadas}
                    onChange={handleChange}
                  />
                  ¿Tiene capacidad para activar rutas compensadas?
                </CheckboxItem>
              </InputGroup>
              <InputGroup>
                <Label>¿Cuáles?</Label>
                <Input
                  type="text"
                  name="rutas_compensadas_cuales"
                  value={formData.rutas_compensadas_cuales}
                  onChange={handleChange}
                  placeholder="Especifique las rutas compensadas disponibles"
                />
              </InputGroup>
            </FormGrid>
          </Section>

          {/* Observaciones */}
          <Section>
            <SectionTitle>
              <FileText size={20} />
              Observaciones Adicionales
            </SectionTitle>
            <InputGroup $fullWidth>
              <Label>Comentarios u Observaciones</Label>
              <Textarea
                name="observaciones"
                value={formData.observaciones}
                onChange={handleChange}
                placeholder="Ingrese cualquier información adicional relevante..."
                rows={4}
              />
            </InputGroup>
          </Section>

          {/* Sección de Términos y Condiciones */}
          <Section>
            <SectionTitle>
              <FileText size={20} />
              Documentos Legales y Aceptaciones
            </SectionTitle>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* Aviso de Privacidad */}
              <CheckboxWithLink>
                <CheckboxLabel>
                  <input
                    type="checkbox"
                    name="acepta_aviso_privacidad"
                    checked={formData.acepta_aviso_privacidad}
                    onChange={handleChange}
                    required
                    style={{ 
                      width: '20px', 
                      height: '20px',
                      cursor: 'pointer',
                      accentColor: '#3b82f6'
                    }}
                  />
                  <span style={{ fontWeight: 500 }}>
                    <RequiredMark>*</RequiredMark> Acepto el Aviso de Privacidad
                  </span>
                </CheckboxLabel>
                <LinkButton type="button" onClick={() => setModalAbierto('aviso')}>
                  Ver documento
                </LinkButton>
              </CheckboxWithLink>

              {/* Declaración de Origen de Fondos */}
              <CheckboxWithLink>
                <CheckboxLabel>
                  <input
                    type="checkbox"
                    name="acepta_origen_fondos"
                    checked={formData.acepta_origen_fondos}
                    onChange={handleChange}
                    required
                    style={{ 
                      width: '20px', 
                      height: '20px',
                      cursor: 'pointer',
                      accentColor: '#3b82f6'
                    }}
                  />
                  <span style={{ fontWeight: 500 }}>
                    <RequiredMark>*</RequiredMark> Acepto la Declaración de Origen de Fondos
                  </span>
                </CheckboxLabel>
                <LinkButton type="button" onClick={() => setModalAbierto('fondos')}>
                  Ver documento
                </LinkButton>
              </CheckboxWithLink>

              {/* Código de Proveedor */}
              <CheckboxWithLink>
                <CheckboxLabel>
                  <input
                    type="checkbox"
                    name="acepta_codigo_proveedor"
                    checked={formData.acepta_codigo_proveedor}
                    onChange={handleChange}
                    required
                    style={{ 
                      width: '20px', 
                      height: '20px',
                      cursor: 'pointer',
                      accentColor: '#3b82f6'
                    }}
                  />
                  <span style={{ fontWeight: 500 }}>
                    <RequiredMark>*</RequiredMark> Acepto el Código de Proveedor y Contratista
                  </span>
                </CheckboxLabel>
                <LinkButton type="button" onClick={() => setModalAbierto('codigo')}>
                  Ver documento
                </LinkButton>
              </CheckboxWithLink>
            </div>
          </Section>

          <ButtonGroup>
            <Button type="button" variant="secondary" onClick={onSwitchToLogin}>
              <ArrowLeft size={16} />
              Volver al Login
            </Button>
            <Button type="submit" disabled={isLoading || uploadingFile}>
              {uploadingFile ? (
                <>
                  <Upload size={16} />
                  Subiendo archivo...
                </>
              ) : isLoading ? (
                'Registrando...'
              ) : (
                <>
                  <UserPlus size={16} />
                  Registrarse
                </>
              )}
            </Button>
          </ButtonGroup>
        </Form>
      </RegisterCard>

      {/* Modal para mostrar documentos */}
      {modalAbierto && (
        <ModalOverlay onClick={() => setModalAbierto(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                {modalAbierto === 'aviso' && 'Aviso de Privacidad'}
                {modalAbierto === 'fondos' && 'Declaración de Origen de Fondos'}
                {modalAbierto === 'codigo' && 'Código de Proveedor y Contratista'}
              </ModalTitle>
              <ModalCloseButton onClick={() => setModalAbierto(null)}>
                <X size={24} />
              </ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              {modalAbierto === 'aviso' && (
                <>
                  <h4>AVISO DE PRIVACIDAD</h4>
                  <p>
                    En mi calidad de titular de la información, autorizo de manera voluntaria, previa, explícita, informada e inequívoca a <strong>TEAM FOODS COLOMBIA S.A</strong> y a <strong>GRASAS S.A</strong> (en adelante, "ALIANZA TEAM") o a cualquiera que detente sus derechos para:
                  </p>
                  <ol>
                    <li>
                      Realizar el tratamiento de mi información personal (nombre, documento de identificación, actividad económica y/o datos de mi empresa, órdenes de pedido, dirección, correo electrónico y teléfono de contacto) suministrada a través del presente documento o a través de cualquier medio, para las siguientes finalidades: a) para proveer servicios y productos requeridos; b) informar sobre cambios, modificaciones o nuevos productos o servicios que estén relacionados o no con el contratado o adquirido por el Titular por cualquier medio de comunicación; c) dar cumplimiento a obligaciones contraídas con el Titular; d) evaluar la calidad del producto y servicio, realizar estudios de mercado y análisis estadísticos para usos internos y la participación de los Titulares en actividades de mercadeo y promocionales; e) facilitar el diseño e implementación de programas de fidelización; f) compartir los Datos Personales, incluyendo la Transferencia y Transmisión de Datos Personales a terceros para fines relacionados con la operación de ALIANZA TEAM; g) realizar estudios internos sobre el cumplimiento de las relaciones comerciales y estudios de mercado a todo nivel; h) llevar a cabo procesos de auditoría interna o externa, propios de la actividad comercial que ALIANZA TEAM desarrolla; i) permitir que compañías vinculadas a ALIANZA TEAM, con las cuales ha celebrado contratos que incluyen disposiciones para garantizar la seguridad y el adecuado Tratamiento de los Datos Personales tratados, contacten al Titular con el propósito de ofrecerle bienes o servicios de su interés; j) controlar el acceso a las oficinas y las plantas de ALIANZA TEAM, incluyendo el establecimiento de zonas video-vigiladas; k) dar respuesta a consultas, peticiones, quejas y reclamos que sean realizadas por los Titulares y a organismos de control y Transmitir los Datos Personales a las demás autoridades que en virtud de la ley aplicable deban recibir los Datos Personales; l) utilizar los distintos servicios correspondientes a sitios web, incluyendo descargas de contenidos y formatos; m) transferir la información recolectada a distintas áreas de ALIANZA TEAM y a sus empresas vinculadas en Colombia y en el exterior cuando ello sea necesario para el desarrollo de sus operaciones y gestión de nómina (recaudo de cartera y cobros administrativos, tesorería, contabilidad, entre otros); n) registrar a los Titulares en los sistemas de ALIANZA TEAM y procesar sus pagos o recaudos; o) prevenir y controlar el riesgo de lavado de activos, la financiación del terrorismo, el financiamiento de la proliferación de armas de destrucción masiva, sus delitos, fuente y la corrupción nacional o transnacional; los datos personales requeridos en el presente documento son recolectados de acuerdo con las directrices e instrucciones de la Superintendencia de Sociedades y la legislación vigente en nuestro país; p) cualquier otra actividad de naturaleza similar y/o complementaria a las anteriormente descritas que sean necesarias para desarrollar el objeto social de ALIANZA TEAM.
                    </li>
                    <li>
                      Consultar y/o verificar la información de mi titularidad depositada o existente en cualquier organismo o entidad de cualquier naturaleza, incluyendo organismos y entidades que recopilan datos de la seguridad social y tratar dicha información con base en lo aquí autorizado, en particular para validación de información y demás finalidades descritas.
                    </li>
                    <li>
                      Que de acuerdo con lo estipulado en el literal b) del artículo 2 de la Ley 1581 de 2012, respecto al ámbito de aplicación y sus excepciones, no se requerirá de una autorización del titular frente al tratamiento de sus datos para las bases de datos y archivos que tengan por finalidad la prevención, detección, monitoreo y control del lavado de activos, el financiamiento del terrorismo y el financiamiento de la proliferación de armas de destrucción masiva, entendiendo así que la autorización del titular proviene de la ley.
                    </li>
                    <li>
                      Conozco que he sido informado de la posibilidad de formular observaciones y reclamos en relación con el tratamiento de mi información a través de los canales habilitados por ALIANZA TEAM para dichos medios.
                    </li>
                  </ol>
                  <p>
                    Declaro que conozco mis derechos como titular de la información de conocer, actualizar, rectificar y suprimir en cualquier momento mis datos personales y revocar la autorización en los casos previstos en la ley. Declaro que he sido informado sobre la naturaleza del tratamiento y los fines para los que son recolectados mis datos personales. Adicionalmente, declaro que según lo establecido en la Ley 1581 de 2012 y la ley 1266 de 2008, he sido informado de la Política de Protección de Datos Personales adoptada por ALIANZA TEAM para el tratamiento de mi información personal, la cual puede ser consultada de forma permanente en la página web de la compañía www.alianzateam.co o en el siguiente enlace:
                  </p>
                  <p>
                    <a href="https://www.alianzateam.com/politica-de-datos-personales-alianza-team/" target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'underline' }}>
                      https://www.alianzateam.com/politica-de-datos-personales-alianza-team/
                    </a>
                  </p>
                </>
              )}

              {modalAbierto === 'fondos' && (
                <>
                  <h4>Declaración de Origen de Fondos</h4>
                  <p><strong>Declaro expresamente que:</strong></p>
                  <ol>
                    <li>
                      Tanto mi actividad, profesión u oficio es lícita y la ejerzo dentro de marco legal y los recursos que poseo no provienen de actividades ilícitas o conductas contempladas en el Código Penal Colombiano o en cualquier norma que lo modifique o adicione.
                    </li>
                    <li>
                      La información que he suministrado en la solicitud de este documento es veraz y verificable y me comprometo a actualizarla anualmente.
                    </li>
                    <li>
                      Las declaraciones obtenidas en este documento son exactas, completas y verificadas en la forma en que aparecen escritas.
                    </li>
                    <li>
                      Eximo a Alianza Team de toda responsabilidad que pudiera llegar a derivarse del incumplimiento de este documento o la información errónea, falsa o inexacta que se haya diligenciado y proporcionado en el mismo.
                    </li>
                    <li>
                      Los recursos que se originen del desarrollo de mi actividad comercial o profesional no provienen ni se destinarán al lavado de activos, financiación del terrorismo, financiamiento de la proliferación de armas de destrucción masiva, sus delitos fuente, corrupción nacional o transnacional.
                    </li>
                    <li>
                      Ninguno de los administradores, ni beneficiarios finales de <span style={{ textDecoration: 'underline', display: 'inline-block', minWidth: '200px', textAlign: 'center' }}>_____________</span> se encuentra reportados en listas restrictivas por autoridades nacionales o extranjeras, por la violación de alguna norma relacionada con los delitos antes mencionados.
                    </li>
                    <li>
                      Los recursos que poseo provienen de las siguientes actividades (detalle, ocupación, actividad o negocio):
                      <br /><br />
                      <strong>Origen Fondos:</strong> <span style={{ textDecoration: 'underline', display: 'inline-block', minWidth: '300px', textAlign: 'center' }}>_______________________</span>
                    </li>
                  </ol>
                </>
              )}

              {modalAbierto === 'codigo' && (
                <>
                  <h4>Acepta el Código de Proveedor</h4>
                  <p>
                    Declaro conocer las Directrices de Política de Alianza Team, al igual que los procedimientos determinados para la prevención de los riesgos de lavado de activos, financiación del terrorismo, financiación de la proliferación de armas de destrucción masiva, sus delitos fuente, corrupción nacional o transnacional. A su vez, acepto el Código del Proveedor y Contratista. Dichos documentos se ubican en el portal web donde he realizado el proceso de inscripción como proveedor, en la sección de documentos a conocer como prerrequisito del inicio o actualización del vínculo.
                  </p>
                  <p>
                    Como constancia de haber leído lo anterior, me comprometo a comunicar a Team Foods Colombia S.A. y/o Grasas S.A. cualquier indicio en mi conocimiento del incumplimiento, o el incumplimiento materializado frente al Código del Proveedor, de manera inmediata.
                  </p>
                  <p>
                    <strong>Para tal efecto, informaré al Oficial de Cumplimiento de Alianza Team:</strong>
                  </p>
                  <ul>
                    <li>
                      <strong>Correo electrónico:</strong> <a href="mailto:cumplimiento@alianzateam.com" style={{ color: '#3b82f6', textDecoration: 'underline' }}>cumplimiento@alianzateam.com</a>
                    </li>
                    <li>
                      <strong>Línea de Integridad:</strong> <a href="http://teamfoods.alertline.com" target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'underline' }}>http://teamfoods.alertline.com</a>
                    </li>
                    <li>
                      <strong>Colombia:</strong> 01800 911 0011 (Una vez comunicado digitar 888-265-9894 y seleccionar el lenguaje. El denunciante recibe un número de caso y pin para regresar al sistema y recibir retroalimentación).
                    </li>
                  </ul>
                </>
              )}
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
    </RegisterContainer>
  );
};

export default Register;
